// Bounded await for the auth boot pipeline (v3.69.2).
//
// Why: the idle+MFA re-login wedge — every awaited Firestore call between
// onAuthStateChanged and setUser used to be unbounded, so a stalled channel
// (the known WebKit stall, src/firebase.js isAppleWebKit note) left the
// Sign In card rendered while Firebase was actually authenticated: no error,
// no retry, nothing. Wrapping those awaits converts a hang into a catchable
// TimeoutError so App.js can show a visible, retryable state instead.
//
// The 15s budget is the app-wide convention — reuse LOAD_TIMEOUT_MS from
// usePageLoad (perf invariant #2: no pending load without a timeout + error
// state); never introduce a second timeout constant.

export class TimeoutError extends Error {
  constructor(label, ms) {
    super(`${label} still pending after ${ms}ms`);
    this.name = 'TimeoutError';
  }
}

/**
 * Race a promise against a timer. Resolves/rejects with the promise if it
 * settles in time; rejects with TimeoutError otherwise. The underlying
 * operation is NOT cancelled (Firestore reads aren't abortable) — callers
 * must treat a timeout as "state unknown", not "operation failed".
 */
export function withTimeout(promise, ms, label = 'operation') {
  let timer;
  const gate = new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(label, ms)), ms);
  });
  return Promise.race([promise, gate]).finally(() => clearTimeout(timer));
}
